/**
/**
 * LessonInfo Component
 *
 * Displays all lesson-related icons and progress components for a course lesson.
 * Renders video, lesson, audio, summary, coevaluation, quiz, and additional material components.
 * Passes relevant props to each child component for dynamic rendering and state management.
 *
 * Features:
 * - Conditional rendering of lesson/video icons based on repository type
 * - Dynamic rendering of audio and summary icons if data is present
 * - Passes userTrack, data, and accessCourse props to child components
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Data object containing lesson info and resources
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @param {Array} props.userTrack - Array of user tracking objects for lesson progress
 * @returns {JSX.Element} The rendered lesson info section
 */
import PropTypes from 'prop-types';
import VideoIconProgress from './VideoIconProgress/VideoIconProgress';
import CoevaluationIconProgress from './CoevaluationIconProgress/CoevaluationIconProgress';
import QuizIconProgress from './QuizIconProgress/QuizIconProgress';
import LessonIconProgress from './LessonIconProgress/LessonIconProgress';
import AditionalMaterialLesson from './AditionalMaterialLesson/AditionalMaterialLesson';
import SummaryIcon from './SummaryIcon/SummaryIcon';
import AudioIconProgress from './AudioIconProgress/AudioIconProgress';
import classes from './LessonInfo.module.css';

const LessonInfo = props => {
  return (
    <div
      className={`${classes.productLessonContainer} ${
        !props.accessCourse ? classes.productLessonDisabled : ''
      }`}
    >
      <aside className={classes.lessonDescription}>
        {/* Conditional rendering: lesson or video icon based on repository type */}
        {props.data.idRepository === 6 ? (
          <LessonIconProgress
            userTrack={props.userTrack} // Prop passed: lesson progress tracking
            data={props.data} // Prop passed: lesson data
            accessCourse={props.accessCourse} // Prop passed: access control
          />
        ) : (
          <VideoIconProgress
            userTrack={props.userTrack} // Prop passed: video progress tracking
            data={props.data} // Prop passed: video data
            accessCourse={props.accessCourse} // Prop passed: access control
          />
        )}

        {/* Conditional rendering: audio icon if pathAudio exists */}
        {props.data.pathAudio && (
          <AudioIconProgress
            userTrack={props.userTrack} // Prop passed: audio progress tracking
            data={props.data} // Prop passed: audio data
            accessCourse={props.accessCourse} // Prop passed: access control
          />
        )}
        {/* Conditional rendering: summary icon if pathSummary exists */}
        {props.data.pathSummary && (
          <SummaryIcon summary={props.data.pathSummary} />
          // Prop passed: summary document URL
        )}
        {/* Coevaluation and quiz icons always rendered */}
        <CoevaluationIconProgress
          userTrack={props.userTrack} // Prop passed: coevaluation progress tracking
          data={props.data} // Prop passed: coevaluation data
          accessCourse={props.accessCourse} // Prop passed: access control
        />
        <QuizIconProgress
          userTrack={props.userTrack} // Prop passed: quiz progress tracking
          data={props.data} // Prop passed: quiz data
          accessCourse={props.accessCourse} // Prop passed: access control
        />
      </aside>
      <AditionalMaterialLesson lessonMaterial={props.data} />
      {/* Prop passed: additional lesson material data */}
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
LessonInfo.propTypes = {
  /**
   * Data object containing lesson info and resources
   * Passed as prop to all child components
   */
  data: PropTypes.object,
  /**
   * Boolean indicating whether the user has access to the course
   * Passed as prop to all child components for access control
   */
  accessCourse: PropTypes.bool,
  /**
   * Array of user tracking objects for lesson progress
   * Passed as prop to all child components for progress tracking
   */
  userTrack: PropTypes.arrayOf(PropTypes.object)
};

LessonInfo.defaultProps = {
  data: {},
  accessCourse: false,
  userTrack: []
};

// ...existing code...

LessonInfo.propTypes = {
  // eslint-disable-next-line react/require-default-props
  data: PropTypes.object,
  // eslint-disable-next-line react/require-default-props
  accessCourse: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  userTrack: PropTypes.object
};

export default LessonInfo;
